import React, { useEffect, useMemo, useState } from 'react';
import { API, getToken } from './adminApi';
import ShortId from './granularInterconnecting/ShortId';
import SuggestionActivity from './granularInterconnecting/SuggestionActivity';
import MemberInfoPanel, { BotChat } from './granularInterconnecting/MemberInfoPanel';
import MemberCard from './granularInterconnecting/MemberCard';
import CommunityHeatMap from './CommunityHeatMap';

// Admin "Community Analytics": one community at a time — top-line numbers
// (age, joins per page visitor, suggestions, interests, matches,
// introductions, distance between members), then every member, sortable by
// location and openable for their details.
// Backend: GET /api/admin/communities/:id/stats and /members.

const PERIODS = [
  { days: 1, label: '1 day' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All time' },
];

// Member table columns — click a heading to sort by it, again to reverse.
const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'location', label: 'Location' },
  { key: 'distance', label: 'Distance', needsFrom: true },
  { key: 'joined', label: 'Joined', firstDir: 'desc' },
  { key: 'lastLogin', label: 'Last logged in', firstDir: 'desc' },
  { key: 'accountCreated', label: 'Account created', firstDir: 'desc' },
  { key: 'visits', label: 'Visits', firstDir: 'desc' },
  { key: 'suggested', label: 'Suggested', firstDir: 'desc' },
  { key: 'interested', label: 'Interested', firstDir: 'desc' },
  { key: 'notInterested', label: 'Not interested', firstDir: 'desc' },
  { key: 'profile', label: 'Profile' },
  { key: 'active', label: 'Active' },
];

// Who's counted and listed: signed-up members, the quick-match waiting list
// (left their email, not joined yet), quick-match searches (no email left),
// members + waiting list, or all three.
const WHO = [
  { key: 'members', label: 'Members', noun: 'signed-up members’ profiles' },
  { key: 'waitlist', label: 'Waiting list', noun: 'waiting-list answers (left their email on the quick-match card)' },
  { key: 'searches', label: 'Searches', noun: 'quick-match searches (no email left)' },
  { key: 'both', label: 'Members + waiting list', noun: 'members’ profiles and waiting-list answers' },
  { key: 'all', label: 'All', noun: 'members’ profiles, waiting-list answers and quick-match searches' },
];
const INCLUDES = {
  members: ['members'], waitlist: ['waitlist'], searches: ['searches'],
  both: ['members', 'waitlist'], all: ['members', 'waitlist', 'searches'],
};

const milesBetween = ([lng1, lat1], [lng2, lat2]) => {
  const rad = (d) => (d * Math.PI) / 180;
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2
    + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(a));
};

const fmtSecs = (n) => (n == null ? null : n >= 60 ? `${Math.floor(n / 60)}m ${n % 60}s` : `${n}s`);

// Hours → "40 min" / "5.2 hours" / "2.4 days".
const fmtGap = (h) => (h == null ? null
  : h < 1 ? `${Math.round(h * 60)} min`
    : h < 48 ? `${Math.round(h * 10) / 10} hours`
      : `${Math.round((h / 24) * 10) / 10} days`);

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
// "Today 14:54", "Yesterday 19:58", "26 Oct, 20:25 (5 days ago)", then
// the date and time.
const fmtAgo = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  const time = t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(t)) / 86400000);
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  const date = t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(days >= 300 ? { year: 'numeric' } : {}) });
  return days < 30 ? `${date}, ${time} (${days} days ago)` : `${date}, ${time}`;
};
const fmtFull = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
// "Order by" for the cards (the table sorts by its column headings too).
const ORDER_BY = [
  { key: 'location', dir: 'asc', label: 'Location' },
  { key: 'name', dir: 'asc', label: 'Name' },
  { key: 'joined', dir: 'desc', label: 'Joined (newest first)' },
  { key: 'lastLogin', dir: 'desc', label: 'Last logged in (most recent first)' },
  { key: 'lastLogin', dir: 'asc', label: 'Last logged in (longest ago first)' },
  { key: 'accountCreated', dir: 'desc', label: 'Account created (newest first)' },
  { key: 'visits', dir: 'desc', label: 'Visits (most first)' },
];

const authed = async (path) => {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Failed');
  return json;
};

const Stat = ({ label, value, sub, onClick, open }) => (
  <div
    className={`as-cs-stat${onClick ? ' as-cs-stat--clickable' : ''}${open ? ' as-cs-stat--open' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
  >
    <div className="as-cs-stat-value">{value ?? '—'}</div>
    <div className="as-cs-stat-label">{label}</div>
    {sub && <div className="as-cs-stat-sub">{sub}</div>}
    {onClick && <div className="as-cs-stat-more">{open ? 'Hide details ▴' : 'More info ▾'}</div>}
  </div>
);

// "2.4 days" etc. for a {mean, median, min, max} in hours / visits.
const SpreadLine = ({ label, v, fmt }) => (v ? (
  <div className="as-cs-spread">
    <strong>{label}:</strong> average {fmt(v.mean)} · typically {fmt(v.median)} · range {fmt(v.min)} – {fmt(v.max)}
  </div>
) : null);

// Sign-ups (joins) per day as a line, or the running total — switchable —
// with an optional forecast: the recent rate (average of the last 4 weeks,
// or the whole period if shorter) carried forward, and the range it would
// likely fall in (Poisson, 90%), since a few sign-ups a day vary a lot.
// Hand-drawn SVG like the Page Analytics charts; hover a point for the day.
const FORECASTS = [[0, 'Off'], [30, '1 month'], [90, '3 months'], [180, '6 months']];
const addDays = (k, n) => { const d = new Date(`${k}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const SignupsChart = ({ data }) => {
  const [mode, setMode] = useState('daily');
  const [ahead, setAhead] = useState(0);
  const [hover, setHover] = useState(null);
  const days = data?.days || [];
  if (!days.length) return null;
  let run = data.before || 0;
  const points = days.map(d => { run += d.count; return { day: d.day, value: mode === 'daily' ? d.count : run, count: d.count }; });
  const lastTotal = run;
  const recent = days.slice(-28);
  const rate = recent.reduce((a, d) => a + d.count, 0) / recent.length;
  const future = Array.from({ length: ahead }, (_, i) => {
    const t = i + 1, expected = rate * t, spread = 1.645 * Math.sqrt(expected);
    return mode === 'daily'
      ? { day: addDays(days[days.length - 1].day, t), value: rate, low: rate, high: rate }
      : { day: addDays(days[days.length - 1].day, t), value: lastTotal + expected, low: lastTotal + Math.max(0, expected - spread), high: lastTotal + expected + spread };
  });
  const all = [...points, ...future];
  const W = 720, H = 222, L = 44, R = 10, T = 12, B = 38;
  const pw = W - L - R, ph = H - T - B;
  const rawMax = Math.max(1, ...points.map(p => p.value), ...future.map(f => f.high));
  // Steps of 1 while the numbers are small; otherwise a round step (2, 5,
  // 10, 20…) giving about five lines.
  const step = rawMax <= 12 ? 1 : [2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000].find(s => rawMax / s <= 6) || Math.ceil(rawMax / 5);
  const max = step * Math.ceil(rawMax / step);
  const yTicks = Array.from({ length: max / step + 1 }, (_, i) => step * i);
  const x = (i) => L + (all.length === 1 ? pw / 2 : (i / (all.length - 1)) * pw);
  const y = (v) => T + ph - (v / max) * ph;
  const line = (pts, offset = 0) => pts.map((p, i) => `${i ? 'L' : 'M'}${x(i + offset).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const n = points.length;
  const futureFrom = future.length ? [{ value: points[n - 1].value }, ...future] : [];
  const band = future.length && mode === 'total'
    ? `M${x(n - 1)},${y(lastTotal)} ${future.map((f, i) => `L${x(n + i)},${y(f.high)}`).join(' ')} ${[...future].reverse().map((f, i) => `L${x(n + future.length - 1 - i)},${y(f.low)}`).join(' ')} Z`
    : null;
  const every = Math.ceil(all.length / 8);
  const label = (k) => new Date(`${k}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const total = days.reduce((a, d) => a + d.count, 0);
  const h = hover != null ? all[hover] : null;
  const isFuture = hover != null && hover >= n;
  const end = future[future.length - 1];
  const fmt1 = (v) => (Math.round(v * 10) / 10).toString();
  return (
    <div style={{ margin: '0.8rem 0 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h3 className="as-section-title" style={{ margin: 0 }}>Sign-ups</h3>
        {[['daily', 'Per day'], ['total', 'Running total']].map(([k, l]) => (
          <button key={k} type="button" className={`as-filter-tab${mode === k ? ' as-filter-tab--active' : ''}`} onClick={() => setMode(k)}>{l}</button>
        ))}
        <span className="as-cs-sep" aria-hidden="true" />
        <span className="as-pa-hint" style={{ margin: 0 }}>Forecast:</span>
        {FORECASTS.map(([d, l]) => (
          <button key={d} type="button" className={`as-filter-tab${ahead === d ? ' as-filter-tab--active' : ''}`} onClick={() => setAhead(d)}>{l}</button>
        ))}
      </div>
      <p className="as-pa-hint" style={{ margin: '0.3rem 0 0' }}>
        {h
          ? isFuture
            ? `${label(h.day)} (forecast): ${mode === 'daily' ? `about ${fmt1(h.value)} a day` : `about ${Math.round(h.value)} in all, likely ${Math.round(h.low)}–${Math.round(h.high)}`}`
            : `${label(h.day)}: ${h.count} joined${mode === 'total' ? ` · ${h.value} in all` : ''}`
          : `${total} joined in this period${mode === 'total' && data.before ? ` · ${data.before} before it` : ''}`}
        {ahead > 0 && !h && ` · At the recent rate (${fmt1(rate)} a day, last ${recent.length} days): about ${Math.round(rate * ahead)} more by ${label(end.day)}`
          + (mode === 'total' ? `, ${Math.round(end.value)} in all (likely ${Math.round(end.low)}–${Math.round(end.high)})` : '')
          + '. Assumes the rate stays the same — ads, seasons and word of mouth will change it.'}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 320, height: 'auto', display: 'block' }} onMouseLeave={() => setHover(null)} role="img" aria-label={mode === 'daily' ? 'Sign-ups per day' : 'Running total of sign-ups'}>
          {yTicks.map(v => (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e2e8f0" />
              <text x={L - 6} y={y(v) + 4} fontSize="11" textAnchor="end" fill="#64748b">{v}</text>
            </g>
          ))}
          {/* Each day, marked subtly: a faint line up the chart and a small tick on the axis. */}
          {all.map((p, i) => (
            <g key={`d${p.day}`}>
              <line x1={x(i)} x2={x(i)} y1={T} y2={T + ph} stroke="#f1f5f9" />
              <line x1={x(i)} x2={x(i)} y1={T + ph} y2={T + ph + 4} stroke="#cbd5e1" />
              {/* Day of the week (M T W T F S S) when there's room for one per day. */}
              {all.length <= 62 && (
                <text x={x(i)} y={T + ph + 14} fontSize="8.5" textAnchor="middle" fill="#94a3b8">{'SMTWTFS'[new Date(`${p.day}T12:00:00`).getDay()]}</text>
              )}
            </g>
          ))}
          {all.map((p, i) => (i % every === 0 || i === all.length - 1) && (
            <text key={p.day} x={x(i)} y={H - 6} fontSize="11" textAnchor="middle" fill="#64748b">{label(p.day)}</text>
          ))}
          {band && <path d={band} fill="#6366f1" opacity="0.12" />}
          {futureFrom.length > 0 && <path d={line(futureFrom, n - 1)} fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="6 5" />}
          <path d={line(points)} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" />
          {all.map((p, i) => (
            <g key={p.day}>
              <rect x={x(i) - pw / all.length / 2} y={T} width={Math.max(3, pw / all.length)} height={ph} fill="transparent" onMouseEnter={() => setHover(i)} />
              {(hover === i || (i < n && n <= 31)) && <circle cx={x(i)} cy={y(p.value)} r={hover === i ? 4.5 : 2.5} fill="#6366f1" />}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

const CommunityStatsTab = () => {
  const [communities, setCommunities] = useState([]);
  const [communityId, setCommunityId] = useState('');
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'location', dir: 'asc' });
  const sortBy = (col) => setSort(prev => (prev.key === col.key
    ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key: col.key, dir: col.firstDir || 'asc' }));
  const [fromUserId, setFromUserId] = useState('');
  const [showTest, setShowTest] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [who, setWho] = useState('members');
  // Members as a sortable table, or as cards like Email → Interconnecting.
  const [view, setView] = useState('table');
  const [info, setInfo] = useState(null); // { userId, botChat } — the Full info panel
  const [rvDetail, setRvDetail] = useState(null); // 'members' | 'signupPage' | 'accountCreated'
  // Returning visitors shown: all, just came back (not logged in), or logged in.
  const [rvWho, setRvWho] = useState('all');
  const toggleRv = (k) => setRvDetail(d => (d === k ? null : k));
  const [waitlist, setWaitlist] = useState(null);
  const [searches, setSearches] = useState(null);

  useEffect(() => {
    fetch(`${API}/communities`)
      .then(r => r.json())
      .then(list => setCommunities((Array.isArray(list) ? list : list.communities || [])
        .map(c => ({ id: String(c._id), title: c.title }))
        .sort((a, b) => a.title.localeCompare(b.title))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!communityId) return;
    let cancelled = false;
    setStats(null);
    setError('');
    authed(`/admin/communities/${communityId}/stats?days=${days}&who=${who}&rv=${rvWho}`)
      .then(s => { if (!cancelled) setStats(s); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [communityId, days, who, rvWho]);

  useEffect(() => {
    // The member lists are per community — nothing to load for "All".
    if (!communityId || communityId === 'all') return;
    let cancelled = false;
    setMembers(null);
    setOpenId(null);
    setFromUserId('');
    authed(`/admin/communities/${communityId}/members`)
      .then(d => { if (!cancelled) setMembers(d.members || []); })
      .catch(err => { if (!cancelled) setError(err.message); });
    // Waiting-list people as rows like members: their email in place of a
    // name, and what they told the quick-match card.
    setWaitlist(null);
    authed(`/quick-match/admin/waitlist?communityId=${communityId}`)
      .then(d => {
        if (cancelled) return;
        const seen = new Set();
        setWaitlist((d.entries || [])
          .filter(e => !e.joined?.inThisCommunity && !seen.has(e.email) && seen.add(e.email))
          .map(e => ({
            userId: `wl:${e._id}`,
            waitlist: true,
            email: e.email,
            firstName: e.email,
            age: e.answers?.Age === '81+' ? 81 : (Number(e.answers?.Age) || null),
            gender: e.answers?.Gender || '',
            localArea: e.localArea || '',
            coordinates: Array.isArray(e.coordinates) && e.coordinates.length === 2 ? e.coordinates : null,
            joinedAt: e.at,
            answers: e.answers || {},
            mainProfileFound: true,
            active: true,
          })));
      })
      .catch(() => { if (!cancelled) setWaitlist([]); });
    // Quick-match searches with no email left, one per browser.
    setSearches(null);
    authed(`/quick-match/admin/searches?communityId=${communityId}`)
      .then(d => {
        if (cancelled) return;
        setSearches((d.entries || []).map(e => ({
          userId: `qs:${e._id}`,
          search: true,
          firstName: `Searcher ${String(e.visitorId || e._id).slice(0, 6)}`,
          age: e.answers?.Age === '81+' ? 81 : (Number(e.answers?.Age) || null),
          gender: e.answers?.Gender || '',
          localArea: e.localArea || '',
          coordinates: Array.isArray(e.coordinates) && e.coordinates.length === 2 ? e.coordinates : null,
          joinedAt: e.at,
          answers: e.answers || {},
          matchCount: e.matchCount,
          mainProfileFound: true,
          active: true,
        })));
      })
      .catch(() => { if (!cancelled) setSearches([]); });
    return () => { cancelled = true; };
  }, [communityId]);

  const from = members?.find(m => String(m.userId) === fromUserId && m.coordinates);
  const sources = { members, waitlist, searches };
  const loaded = INCLUDES[who].every(k => sources[k]);
  const list = useMemo(() => {
    if (!loaded) return [];
    const rows = INCLUDES[who].flatMap(k => sources[k] || [])
      .filter(m => showTest || m.mainProfileFound)
      .map(m => ({
        ...m,
        miles: from && m.coordinates ? milesBetween(from.coordinates, m.coordinates) : null,
        // Times suggested someone here, and their answers (from /stats).
        counts: m.waitlist || m.search ? null : (stats?.perMember?.[String(m.userId)] || { suggested: 0, interested: 0, notInterested: 0 }),
      }));
    // The value each column sorts on; blank (null / '') always goes last,
    // whichever way round.
    const value = {
      name: m => (m.firstName || '').toLowerCase() || null,
      age: m => m.age ?? null,
      gender: m => m.gender || null,
      location: m => (m.localArea || '').toLowerCase() || null,
      distance: m => m.miles,
      joined: m => (m.joinedAt ? new Date(m.joinedAt).getTime() : null),
      lastLogin: m => (m.lastLoginAt ? new Date(m.lastLoginAt).getTime() : null),
      accountCreated: m => (m.accountCreatedAt ? new Date(m.accountCreatedAt).getTime() : null),
      visits: m => (m.waitlist || m.search ? null : m.visits ?? 0),
      profile: m => (m.waitlist || m.search ? 100 : m.profileCheck?.complete ? 0 : (m.profileCheck?.missing || []).length || 99),
      active: m => (m.active ? 0 : 1),
      suggested: m => m.counts?.suggested ?? null,
      interested: m => m.counts?.interested ?? null,
      notInterested: m => m.counts?.notInterested ?? null,
    }[sort.key === 'distance' && !from ? 'location' : sort.key];
    const flip = sort.dir === 'desc' ? -1 : 1;
    return rows.sort((a, b) => {
      const x = value(a);
      const y = value(b);
      if (x == null || y == null) return x == null && y == null ? 0 : x == null ? 1 : -1;
      return (typeof x === 'string' ? x.localeCompare(y) : x - y) * flip;
    });
  }, [loaded, members, waitlist, searches, who, showTest, sort, from, stats]); // eslint-disable-line react-hooks/exhaustive-deps
  const testCount = members ? members.filter(m => !m.mainProfileFound).length : 0;

  const modeText = stats?.age
    ? (stats.age.mode.length === 0 ? 'None — no two share an age' : stats.age.mode.join(', '))
    : null;

  return (
    <div className="as-activity">
      {error && <div className="as-error">{error}</div>}
      <div className="as-activity-toolbar">
        <h2 className="as-section-title" style={{ margin: 0 }}>Community Analytics</h2>
        <select className="as-pa-select" value={communityId} onChange={e => setCommunityId(e.target.value)} aria-label="Community">
          <option value="">Choose a community…</option>
          <option value="all">All communities</option>
          {communities.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {communityId && WHO.map(w => (
          <button key={w.key} type="button" className={`as-filter-tab${who === w.key ? ' as-filter-tab--active' : ''}`} onClick={() => setWho(w.key)}>{w.label}</button>
        ))}
        {communityId && <span className="as-cs-sep" aria-hidden="true" />}
        {communityId && PERIODS.map(p => (
          <button key={p.days} type="button" className={`as-filter-tab${days === p.days ? ' as-filter-tab--active' : ''}`} onClick={() => setDays(p.days)}>{p.label}</button>
        ))}
      </div>

      {!communityId && <p className="as-pa-hint">Pick a community to see its numbers and members.</p>}

      {communityId && !stats && !error && <div className="as-loading">Loading…</div>}
      {stats && (
        <div className="as-pa-card">
          {/* Who and when these numbers cover — up front, easy to see. */}
          <div className="as-cs-banner">
            <div className="as-cs-banner-chips">
              <span className="as-cs-chip as-cs-chip--period">
                📅 {days === 1 ? 'Last day' : days ? `Last ${days} days` : 'All time'}
              </span>
              {who !== 'waitlist' && who !== 'searches' && (
                <span className="as-cs-chip">
                  <strong>{stats.counted?.members ?? stats.members}</strong> {(stats.counted?.members ?? stats.members) === 1 ? 'member' : 'members'}
                  {days ? <span className="as-cs-chip-sub"> joined · {stats.members} in all</span> : null}
                </span>
              )}
              {stats.membersActive && who !== 'waitlist' && who !== 'searches' && (
                <span className="as-cs-chip" title="Members who used the app logged in during this period (from their last sign-in / session)">
                  🔑 <strong>{stats.membersActive.active}</strong> of {stats.membersActive.of} members logged in
                </span>
              )}
              {['waitlist', 'both', 'all'].includes(who) && (
                <span className="as-cs-chip"><strong>{stats.counted?.waitlist ?? stats.waitlist}</strong> on the waiting list</span>
              )}
              {['searches', 'all'].includes(who) && (
                <span className="as-cs-chip"><strong>{stats.counted?.searchers ?? stats.searchers}</strong> {(stats.counted?.searchers ?? stats.searchers) === 1 ? 'searcher' : 'searchers'}</span>
              )}
            </div>
            {/* Members counted, by how far they've got with their profile. */}
            {who !== 'waitlist' && who !== 'searches' && stats.profiles && (stats.counted?.members ?? 0) > 0 && (
              <div className="as-cs-profile-split">
                <span className="as-cs-pill as-cs-pill--complete">✅ <strong>{stats.profiles.complete}</strong> complete with photo</span>
                <span className="as-cs-pill as-cs-pill--nophoto">📷 <strong>{stats.profiles.noPhoto}</strong> complete, no photo</span>
                <span className="as-cs-pill as-cs-pill--incomplete">🚧 <strong>{stats.profiles.incomplete}</strong> signed up, profile incomplete</span>
              </div>
            )}
            <ul className="as-cs-banner-notes">
              <li>Everything below is for this period: the people who joined (or searched / left their email) in it, and the suggestions, answers, introductions and visits in it.</li>
              <li>Test accounts, your own and admin / test browsers are left out.</li>
              <li>&quot;Logged in&quot; = members who used the app (Messenger, profile, etc.) while signed in during this period — not just visits to this page.</li>
              {who !== 'members' && <li>Suggestions, interests, matches and introductions are for members only.</li>}
              <li>Complete = can be suggested (name, date of birth, location, 18+, Bio or a profile answer). A photo means a real one, not the automatic avatar.</li>
            </ul>
          </div>
          {who !== 'waitlist' && who !== 'searches' && <SignupsChart data={stats.signupsDaily} />}
          <div className="as-cs-grid">
            <Stat label={stats.age.mode.length > 1 ? `Most common ages (${stats.age.mode.length} tied)` : 'Most common age'} value={modeText} sub={stats.age.modeCount ? `${stats.age.modeCount} members each` : `${stats.age.withAge} with an age`} />
            <Stat label="Average age" value={stats.age.mean} sub={`${stats.age.withAge} of ${stats.people} with an age`} />
            <Stat
              label={{ members: 'Joins per page visitor', waitlist: 'Waiting-list sign-ups per page visitor', searches: 'Searches per page visitor' }[who] || 'Joins, emails and searches per page visitor'}
              value={stats.joinRate != null ? `${stats.joinRate}%` : null}
              sub={`${stats.joins} ${{ members: 'joins', waitlist: 'emails', searches: 'searchers' }[who] || 'people'} / ${stats.visitors.toLocaleString()} visitors`}
            />
            <Stat label="Average time on page" value={fmtSecs(stats.timeOnPage?.meanSeconds)} sub={stats.timeOnPage?.views ? `median ${fmtSecs(stats.timeOnPage.medianSeconds)} · ${stats.timeOnPage.views.toLocaleString()} visits` : 'no visits'} />
            <Stat label="Suggestions made" value={stats.suggestions} />
            <Stat label="One-way interests" value={stats.oneWay} sub="one said yes, waiting on the other" />
            <Stat label="'Not interested' given" value={stats.notInterested} sub="answers of not interested to a suggestion" />
            <Stat label="Matches" value={stats.matches} sub="both said yes" />
            <Stat label="Introductions made" value={stats.connections} sub="put in touch in Messenger" />
            <Stat
              label="Typical distance to nearest other member"
              value={stats.distance.medianNearestMiles != null ? `${stats.distance.medianNearestMiles} mi` : null}
              sub={stats.distance.medianNearestMiles != null
                ? `${stats.distance.withinCount} of ${stats.distance.located} have someone within ${stats.distance.withinMiles} mi · mean ${stats.distance.averageNearestMiles} mi`
                : `${stats.distance.located} located`}
            />
            <Stat label="Average distance between all members" value={stats.distance.averageMiles != null ? `${stats.distance.averageMiles} mi` : null} sub="every pair, wherever they are" />
          </div>
          {/* Where they are — heat map of the people counted. */}
          <h3 className="as-section-title" style={{ margin: '1rem 0 0.2rem' }}>Where they are</h3>
          <p className="as-pa-hint" style={{ marginTop: 0 }}>
            {stats.mapPoints?.length || 0} with a location, from {WHO.find(w => w.key === who).noun} (rounded to about 1 km). Darker = more people.
          </p>
          <CommunityHeatMap points={stats.mapPoints || []} />

          {/* Age histogram, in 6-year bands up to 99. */}
          {stats.age?.histogram && (() => {
            const h = stats.age.histogram;
            const top = Math.max(1, ...h.map(b => b.count));
            const total = h.reduce((n, b) => n + b.count, 0);
            return (
              <>
                <h3 className="as-section-title" style={{ margin: '1rem 0 0.2rem' }}>Ages</h3>
                <p className="as-pa-hint" style={{ marginTop: 0 }}>
                  {total} with an age, from {WHO.find(w => w.key === who).noun}.
                </p>
                <div className="as-cs-hist" role="img" aria-label={h.map(b => `${b.band}: ${b.count}`).join(', ')}>
                  {h.map(b => (
                    <div key={b.band} className="as-cs-hist-col">
                      <div className="as-cs-hist-count">{b.count || ''}</div>
                      <div className="as-cs-hist-bar" style={{ height: `${(b.count / top) * 100}%` }} title={`${b.band}: ${b.count} (${total ? Math.round((b.count / total) * 100) : 0}%)`} />
                      {/* Can break after the dash, so 14 bands fit on a phone. */}
                      <div className="as-cs-hist-label">{b.band.replace('–', '–\u200b')}</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          {stats.returningVisitors && (
            <>
              <h3 className="as-section-title" style={{ margin: '1rem 0 0.2rem' }}>Returning visitors</h3>
              <div className="as-cs-rv-toggle" role="group" aria-label="Which returning visitors">
                {[
                  ['all', `All returning (${stats.returningVisitors.allReturning ?? stats.returningVisitors.returning})`],
                  ['visitors', `Just came back — not logged in (${(stats.returningVisitors.allReturning ?? 0) - (stats.returningVisitors.loggedInReturning ?? 0)})`],
                  ['loggedin', `Already logged in (${stats.returningVisitors.loggedInReturning ?? 0})`],
                ].map(([k, label]) => (
                  <button key={k} type="button" className={`as-filter-tab${rvWho === k ? ' as-filter-tab--active' : ''}`} onClick={() => setRvWho(k)}>{label}</button>
                ))}
              </div>
              <div className="as-cs-callout">
                <strong>Visitors = people opening this community&apos;s page on the website</strong>, logged in or not — counted by their browser, not by account.
                Most aren&apos;t logged in (they&apos;re usually arriving from an ad); &quot;returning&quot; just means the same browser came back to the page.
              </div>
              <p className="as-pa-hint" style={{ marginTop: 0 }}>
                Visitors to this community&apos;s page {days === 1 ? 'in the last day' : days ? `in the last ${days} days` : 'ever'} (admin / test browsers left out).
                A new visit starts after 30 minutes away; returning = 2+ visits, or they&apos;d been before. Time between visits includes the gap back to their last visit before this period.
              </p>
              <div className="as-cs-grid">
                <Stat
                  label="Returning website visitors"
                  value={stats.returningVisitors.returning.toLocaleString()}
                  sub={(
                    <>
                      {stats.returningVisitors.share ?? 0}% of {stats.returningVisitors.visitors.toLocaleString()} people who opened the page
                      <br />
                      <strong>{stats.returningVisitors.returning - (stats.returningVisitors.loggedInReturning ?? 0)}</strong> just came back to the page (not logged in)
                      <br />
                      <strong>{stats.returningVisitors.loggedInReturning ?? 0}</strong> were already logged in when they opened the page
                    </>
                  )}
                />
                {stats.membersActive && (
                  <Stat
                    label="Members who logged in"
                    value={`${stats.membersActive.active} / ${stats.membersActive.of}`}
                    sub="used the app signed in this period (Messenger, profile…) — not counted as page visitors above"
                  />
                )}
                <Stat
                  label="Visits per returning visitor (this period)"
                  value={stats.returningVisitors.visitsPerReturning.mean}
                  sub={stats.returningVisitors.visitsPerReturning.max != null ? `typically ${stats.returningVisitors.visitsPerReturning.median} · most ${stats.returningVisitors.visitsPerReturning.max}` : null}
                />
                <Stat
                  label="Typical time between visits"
                  value={fmtGap(stats.returningVisitors.gapHours.median)}
                  sub={stats.returningVisitors.gapHours.count ? `average ${fmtGap(stats.returningVisitors.gapHours.mean)} · ${stats.returningVisitors.gapHours.count} returns` : 'no repeat visits yet'}
                />
                <Stat
                  label="Range between visits"
                  value={stats.returningVisitors.gapHours.count ? `${fmtGap(stats.returningVisitors.gapHours.min)} – ${fmtGap(stats.returningVisitors.gapHours.max)}` : null}
                  sub="shortest – longest gap"
                />
                <Stat
                  label="When they come back"
                  value={stats.returningVisitors.gapHours.count ? `${Math.round((stats.returningVisitors.gapBands.withinWeek + stats.returningVisitors.gapBands.withinDay) / stats.returningVisitors.gapHours.count * 100)}% within a week` : null}
                  sub={`${stats.returningVisitors.gapBands.withinDay} within a day · ${stats.returningVisitors.gapBands.withinWeek} within a week · ${stats.returningVisitors.gapBands.later} later`}
                />
                <Stat
                  label="Already logged in when they visited"
                  value={`${stats.returningVisitors.loggedInReturning ?? 0} / ${stats.returningVisitors.returning}`}
                  sub={`of returning visitors · ${stats.returningVisitors.loggedInAll ?? 0} of all ${stats.returningVisitors.visitors.toLocaleString()} visitors`}
                />
                <Stat
                  onClick={() => toggleRv('members')}
                  open={rvDetail === 'members'}
                  label="Already members"
                  value={`${stats.returningVisitors.members} / ${stats.returningVisitors.returning}`}
                  sub={`${stats.returningVisitors.returning ? Math.round(stats.returningVisitors.members / stats.returningVisitors.returning * 100) : 0}% of returning visitors had joined`}
                />
                <Stat
                  onClick={() => toggleRv('signupPage')}
                  open={rvDetail === 'signupPage'}
                  label="Went to the sign-up page"
                  value={`${stats.returningVisitors.signupPage} / ${stats.returningVisitors.returning}`}
                  sub={`${stats.returningVisitors.returning ? Math.round(stats.returningVisitors.signupPage / stats.returningVisitors.returning * 100) : 0}% of returning visitors`}
                />
                <Stat
                  onClick={() => toggleRv('accountCreated')}
                  open={rvDetail === 'accountCreated'}
                  label="Created an account"
                  value={`${stats.returningVisitors.accountCreated} / ${stats.returningVisitors.returning}`}
                  sub={`${stats.returningVisitors.accountRateReturning ?? 0}% of returning · ${stats.returningVisitors.accountRateNew ?? 0}% of one-visit visitors`}
                />
                {stats.returningVisitors.cookies && (() => {
                  const c = stats.returningVisitors.cookies;
                  const pctOf = (n, d) => (d ? `${Math.round((n / d) * 1000) / 10}%` : '—');
                  return (
                    <Stat
                      onClick={() => toggleRv('cookies')}
                      open={rvDetail === 'cookies'}
                      label="Accepted cookies"
                      value={c.returning.recorded ? `${c.returning.accepted} / ${c.returning.recorded}` : 'Not yet recorded'}
                      sub={c.returning.recorded || c.oneVisit.recorded
                        ? `${pctOf(c.returning.accepted, c.returning.recorded)} of returning · ${pctOf(c.oneVisit.accepted, c.oneVisit.recorded)} of one-visit visitors`
                        : 'only newer visits record it'}
                    />
                  );
                })()}
              </div>
              {rvDetail === 'cookies' && stats.returningVisitors.cookies && (() => {
                const c = stats.returningVisitors.cookies;
                const row = (label, t) => (
                  <tr key={label}>
                    <td><strong>{label}</strong></td>
                    <td className="as-pa-num">{t.accepted}</td>
                    <td className="as-pa-num">{t.declined}</td>
                    <td className="as-pa-num">{t.none}</td>
                    <td className="as-pa-num">{t.recorded}</td>
                  </tr>
                );
                return (
                  <div className="as-cs-detail">
                    <div className="as-cs-tally-title">Cookie banner choices</div>
                    <p className="as-card-meta" style={{ margin: '0 0 0.4rem' }}>
                      Only visits since this started being recorded have the choice, so earlier visitors aren&apos;t counted. The Meta Pixel and Google Analytics only run for those who accepted.
                    </p>
                    <div className="as-act-table-wrap">
                      <table className="as-act-table">
                        <thead><tr><th></th><th>Accepted</th><th>Declined</th><th>No choice yet</th><th>Recorded</th></tr></thead>
                        <tbody>{row('Returning visitors', c.returning)}{row('One-visit visitors', c.oneVisit)}</tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              {rvDetail && rvDetail !== 'cookies' && stats.returningVisitors.details?.[rvDetail] && (() => {
                const d = stats.returningVisitors.details[rvDetail];
                const title = { members: 'Returning visitors who are members', signupPage: 'Returning visitors who went to the sign-up page', accountCreated: 'Returning visitors who created an account' }[rvDetail];
                const action = { signupPage: 'the sign-up page', accountCreated: 'creating an account' }[rvDetail];
                return (
                  <div className="as-cs-detail">
                    <div className="as-cs-tally-title">{title} ({d.people.length})</div>
                    <SpreadLine label={`Time from first visit to ${action}`} v={d.hoursToAction} fmt={(h) => (h === 0 ? 'straight away' : fmtGap(h))} />
                    <SpreadLine label={rvDetail === 'members' ? 'Visits' : `Visits up to ${action}`} v={rvDetail === 'members' ? d.visits : d.visitsBefore} fmt={(n) => `${n}`} />
                    <div className="as-act-table-wrap">
                      <table className="as-act-table">
                        <thead>
                          <tr>
                            <th>Visitor</th><th>First visit</th><th>Came from</th><th>Visits</th>
                            {action && <th>Time to {rvDetail === 'signupPage' ? 'sign-up page' : 'account'}</th>}
                            {action && <th>Visits up to it</th>}
                            <th>Route (each visit)</th><th>Area</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.people.map(p => (
                            <tr key={p.visitor}>
                              <td className="as-card-meta" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.visitor}</td>
                              <td className="as-act-time">{fmtDay(p.firstVisit)}</td>
                              <td>{p.firstSource}</td>
                              <td className="as-pa-num">{p.visits}</td>
                              {action && <td>{p.hours == null ? '—' : p.hours === 0 ? 'First visit' : fmtGap(p.hours)}</td>}
                              {action && <td className="as-pa-num">{p.visitsBefore ?? '—'}</td>}
                              <td style={{ fontSize: '0.8rem' }}>{p.route.map((x, i) => <div key={i}>{i + 1}. {x}</div>)}</td>
                              <td>{p.area || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              <div className="as-cs-tallies">
                {[
                  ['First came from', stats.returningVisitors.firstSources],
                  ['Came back via', stats.returningVisitors.returnSources],
                  ['Went next to', stats.returningVisitors.nextSteps],
                  ['Where they are (from a quick-match search)', stats.returningVisitors.areas],
                ].map(([title, items]) => (
                  <div key={title} className="as-cs-tally">
                    <div className="as-cs-tally-title">{title}</div>
                    {items?.length ? items.map(i => (
                      <div key={i.label} className="as-cs-tally-row"><span>{i.label}</span><strong>{i.count}</strong></div>
                    )) : <div className="as-card-meta">—</div>}
                  </div>
                ))}
              </div>
              {stats.returningVisitors.recent?.length > 0 && (
                <details className="as-cs-recent">
                  <summary>Recent returning visitors and their route ({stats.returningVisitors.recent.length})</summary>
                  <div className="as-act-table-wrap">
                    <table className="as-act-table">
                      <thead><tr><th>Visitor</th><th>Visits (how they arrived → where next)</th><th>Member</th><th>Sign-up page</th><th>Account</th><th>Area</th></tr></thead>
                      <tbody>
                        {stats.returningVisitors.recent.map(r => (
                          <tr key={r.visitor}>
                            <td className="as-card-meta" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{r.visitor}</td>
                            <td style={{ fontSize: '0.82rem' }}>
                              {r.visits.map((v, i) => (
                                <div key={i}>
                                  <span className="as-act-time">{fmtDay(v.at)}</span> · {v.source}{v.next.length ? ` → ${v.next.join(', ')}` : ''}
                                </div>
                              ))}
                            </td>
                            <td>{r.member ? 'Yes' : '—'}</td>
                            <td>{r.signupPage ? 'Yes' : '—'}</td>
                            <td>{r.accountCreated ? '✅' : '—'}</td>
                            <td>{r.area || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
          {stats.fields?.length > 0 && (
            <>
              <h3 className="as-section-title" style={{ margin: '1rem 0 0.2rem' }}>
                {who === 'members' ? 'Answers in signed-up members’ profiles' : who === 'searches' ? 'Answers given in quick-match searches' : who === 'waitlist' ? 'Waiting-list answers' : 'Answers'}
              </h3>
              <p className="as-pa-hint" style={{ marginTop: 0 }}>
                From {WHO.find(w => w.key === who).noun}. Share = of those who answered that question (blank answers aren’t counted).
              </p>
              <div className="as-act-table-wrap">
                <table className="as-act-table">
                  <thead><tr><th>Question</th><th>Most common</th><th>Share</th><th>Average</th><th>Top answers</th></tr></thead>
                  <tbody>
                    {stats.fields.map(f => (
                      <tr key={f.name}>
                        <td><strong>{f.name}</strong>{f.multi && <span className="as-card-meta"> (tick any)</span>}{f.slider && <span className="as-card-meta"> (slider)</span>}</td>
                        <td>{f.mode.length ? f.mode.join(' · ') : '—'}</td>
                        <td className="as-pa-num">{f.modeShare != null ? `${f.modeShare}%` : '—'}<div className="as-card-meta">{f.answered ? `${f.modeCount} of ${f.answered}` : 'no answers'}</div></td>
                        <td className="as-pa-num">{f.mean ?? '—'}{f.meanNote && <div className="as-card-meta">{f.meanNote}</div>}</td>
                        <td style={{ fontSize: '0.82rem' }}>{f.top.map(t => `${t.value} (${t.count})`).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {communityId === 'all' && (
        <div className="as-pa-card">
          <p className="as-pa-hint" style={{ margin: 0 }}>
            Every community together above (someone in two communities counts once; only gender is summarised from profiles, as each community asks different questions). Choose a single community to see its members.
          </p>
        </div>
      )}
      {communityId && communityId !== 'all' && (
        <div className="as-pa-card">
          <div className="as-pa-card-head">
            <h3 className="as-section-title">{WHO.find(w => w.key === who).label === 'Members' ? 'Members' : WHO.find(w => w.key === who).label} ({list.length})</h3>
            <span style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Adds a Distance column (sorted nearest first) from this member. */}
              <select
                className="as-pa-select"
                value={fromUserId}
                onChange={e => { setFromUserId(e.target.value); if (e.target.value) setSort({ key: 'distance', dir: 'asc' }); }}
                aria-label="Distance from"
              >
                <option value="">Distance from… (choose a member)</option>
                {(members || []).filter(m => m.coordinates && m.mainProfileFound).map(m => (
                  <option key={m.userId} value={String(m.userId)}>{m.firstName || '(no name)'}{m.localArea ? ` — ${m.localArea}` : ''}</option>
                ))}
              </select>
              <select
                className="as-pa-select"
                value={ORDER_BY.findIndex(o => o.key === sort.key && o.dir === sort.dir)}
                onChange={e => { const o = ORDER_BY[Number(e.target.value)]; if (o) setSort({ key: o.key, dir: o.dir }); }}
                aria-label="Order by"
              >
                {ORDER_BY.findIndex(o => o.key === sort.key && o.dir === sort.dir) === -1 && <option value={-1}>Order by…</option>}
                {ORDER_BY.map((o, i) => <option key={i} value={i}>Order by: {o.label}</option>)}
              </select>
              <button type="button" className={`as-filter-tab${view === 'table' ? ' as-filter-tab--active' : ''}`} onClick={() => setView('table')}>Table</button>
              <button type="button" className={`as-filter-tab${view === 'cards' ? ' as-filter-tab--active' : ''}`} onClick={() => setView('cards')}>Cards</button>
              {testCount > 0 && (
                <label className="as-card-meta" style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                  <input type="checkbox" checked={showTest} onChange={e => setShowTest(e.target.checked)} />
                  Show {testCount} test / orphan records
                </label>
              )}
            </span>
          </div>
          {!loaded && !error && <div className="as-loading">Loading…</div>}
          {loaded && view === 'cards' && (
            <>
              {from && (
                <div className="as-radius-tool">
                  <span>↕ Sorted nearest to furthest from <strong>{from.firstName || '(no name)'}</strong><ShortId userId={from.userId} />{from.localArea ? ` (${from.localArea})` : ''}</span>
                  <button type="button" className="as-filter-tab" onClick={() => { setFromUserId(''); setSort({ key: 'location', dir: 'asc' }); }}>Clear sort</button>
                </div>
              )}
              <div className="as-signup-list as-signup-list--grid">
                {list.length === 0 && <p className="as-act-empty">No members</p>}
                {list.map(m => (
                  <MemberCard
                    key={m.userId}
                    member={m}
                    meta={(
                      <>
                        {m.waitlist && <span className="as-cs-badge">waiting list</span>}
                        {m.search && <span className="as-cs-badge as-cs-badge--search">searched</span>}
                        {from?.userId === m.userId && <span className="as-radius-badge"> · ↕ sorting from here</span>}
                        {from && from.userId !== m.userId && m.miles != null && <span> · {m.miles < 10 ? m.miles.toFixed(1) : Math.round(m.miles)} miles away</span>}
                        {!m.waitlist && !m.search && <span title={fmtFull(m.lastLoginAt)}> · Last logged in: {fmtAgo(m.lastLoginAt)}</span>}
                        {!m.waitlist && !m.search && <span> · Account created: {fmtFull(m.accountCreatedAt) || '—'} · Visits: {m.visits ?? 0}</span>}
                      </>
                    )}
                    note={!m.waitlist && !m.search && !m.profileCheck?.complete && (m.profileCheck?.missing || []).length > 0 && (
                      <div className="as-card-meta">Missing: {m.profileCheck.missing.join(', ')}</div>
                    )}
                  >
                    {!m.waitlist && !m.search && (
                      <>
                        <button type="button" className="as-text-link" onClick={() => setInfo({ userId: m.userId, botChat: false })}>Full info</button>
                        <button type="button" className="as-text-link" onClick={() => setInfo({ userId: m.userId, botChat: true })}>Bot chat</button>
                      </>
                    )}
                    {m.waitlist && <a className="as-text-link" href={`mailto:${m.email}`}>Email them</a>}
                    {m.coordinates && from?.userId !== m.userId && !m.waitlist && !m.search && (
                      <button type="button" className="as-text-link" onClick={() => { setFromUserId(String(m.userId)); setSort({ key: 'distance', dir: 'asc' }); }}>Sort nearest to here</button>
                    )}
                  </MemberCard>
                ))}
              </div>
            </>
          )}
          {loaded && view === 'table' && (
            <div className="as-act-table-wrap">
              <table className="as-act-table">
                <thead>
                  <tr>
                    {COLUMNS.filter(c => !c.needsFrom || from).map(c => (
                      <th key={c.key} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" className={`as-cs-sort${sort.key === c.key ? ' as-cs-sort--on' : ''}`} onClick={() => sortBy(c)}>
                          {c.label}
                          <span className="as-cs-sort-arrow" aria-hidden="true">{sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && <tr><td colSpan="12" className="as-act-empty">No members</td></tr>}
                  {list.map(m => {
                    const open = openId === m.userId;
                    return (
                      <React.Fragment key={m.userId}>
                        <tr className="as-cs-row" onClick={() => setOpenId(open ? null : m.userId)} title="Show details">
                          <td>
                            {m.photo && <img src={m.photo} alt="" className="as-cs-thumb" />}
                            {m.search
                              ? <>{m.firstName} <span className="as-cs-badge as-cs-badge--search">searched</span></>
                              : m.waitlist
                              ? <>{m.email} <span className="as-cs-badge">waiting list</span></>
                              : <>{m.firstName || '(no name)'}<ShortId userId={m.userId} /></>}
                          </td>
                          <td className="as-pa-num">{m.age ?? '—'}</td>
                          <td>{m.gender || '—'}</td>
                          <td>{m.localArea || '—'}</td>
                          {from && <td className="as-pa-num">{m.miles != null ? `${Math.round(m.miles)} mi` : '—'}</td>}
                          <td className="as-act-time">{fmtDay(m.joinedAt)}</td>
                          <td className="as-act-time" title={fmtFull(m.lastLoginAt)}>{m.waitlist || m.search ? '—' : fmtAgo(m.lastLoginAt)}</td>
                          <td className="as-act-time">{m.waitlist || m.search ? '—' : (fmtFull(m.accountCreatedAt) || '—')}</td>
                          <td className="as-pa-num" title={m.lastVisitAt ? `Last visit ${fmtFull(m.lastVisitAt)} · counted since 1 Nov 2026` : 'Counted since 1 Nov 2026'}>{m.waitlist || m.search ? '—' : (m.visits ?? 0)}</td>
                          <td className="as-pa-num">{m.counts ? m.counts.suggested : '—'}</td>
                          <td className="as-pa-num">{m.counts ? m.counts.interested : '—'}</td>
                          <td className="as-pa-num">{m.counts ? m.counts.notInterested : '—'}</td>
                          <td>{m.search ? `Search · ${m.matchCount ?? 0} matches` : m.waitlist ? 'Not signed up' : m.profileCheck?.complete ? '✅ Complete' : `Missing: ${(m.profileCheck?.missing || []).join(', ') || '—'}`}</td>
                          <td>{m.waitlist || m.search ? '—' : m.active ? 'Yes' : 'Paused'}</td>
                        </tr>
                        {open && m.search && (
                          <tr className="as-cs-details">
                            <td colSpan={from ? 14 : 13}>
                              <dl className="as-cs-dl">
                                {Object.entries(m.answers).map(([k, v]) => (
                                  <React.Fragment key={k}><dt>{k}</dt><dd>{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>
                                ))}
                              </dl>
                              <p className="as-card-meta" style={{ marginTop: '0.5rem' }}>Searched the quick-match card and left no email — we can&apos;t contact them.</p>
                            </td>
                          </tr>
                        )}
                        {open && m.waitlist && (
                          <tr className="as-cs-details">
                            <td colSpan={from ? 14 : 13}>
                              <dl className="as-cs-dl">
                                <dt>Email</dt><dd><a href={`mailto:${m.email}`}>{m.email}</a></dd>
                                {Object.entries(m.answers).map(([k, v]) => (
                                  <React.Fragment key={k}><dt>{k}</dt><dd>{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>
                                ))}
                              </dl>
                              <p className="as-card-meta" style={{ marginTop: '0.5rem' }}>On the waiting list — no suggestions or bot chat until they sign up.</p>
                            </td>
                          </tr>
                        )}
                        {open && !m.waitlist && !m.search && (
                          <tr className="as-cs-details">
                            <td colSpan={from ? 14 : 13}>
                              {m.pics?.length > 0 && (
                                <div className="as-cs-pics">{m.pics.map(p => <img key={p} src={p} alt="" />)}</div>
                              )}
                              <dl className="as-cs-dl">
                                {Object.entries(m.communityProfile || {}).filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length)).map(([k, v]) => (
                                  <React.Fragment key={k}><dt>{k}</dt><dd>{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>
                                ))}
                                {m.seeking && <><dt>Looking for</dt><dd>{m.seeking}</dd></>}
                                {m.botNotes && <><dt>Bot notes</dt><dd>{m.botNotes}{m.maxDistanceMiles ? ` (within ${m.maxDistanceMiles} mi)` : ''}</dd></>}
                                <dt>Groups</dt><dd>{(m.joinedCommunities || []).map(c => c.communityTitle || c.communityId).join(', ') || '—'}</dd>
                                {m.meetChats?.length > 0 && <><dt>/meet chats</dt><dd>{m.meetChats.length}</dd></>}
                              </dl>
                              {/* Suggestions they've been given (and how each side answered), and
                                  their chat with this community's bot — loaded when opened. */}
                              <div className="as-cs-panels">
                                <section>
                                  <h4 className="as-cs-panel-title">Suggestions given</h4>
                                  <SuggestionActivity communityId={communityId} userId={m.userId} authedFetch={authed} memberName={m.firstName || 'They'} />
                                </section>
                                <section>
                                  <h4 className="as-cs-panel-title">Bot chat</h4>
                                  <BotChat communityId={communityId} userId={m.userId} authedFetch={authed} />
                                </section>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {info && members && (
        <MemberInfoPanel
          member={members.find(mm => mm.userId === info.userId)}
          communityId={communityId}
          communityTitle={stats?.communityTitle || ''}
          focusBotChat={info.botChat}
          authedFetch={authed}
          onClose={() => setInfo(null)}
        />
      )}
    </div>
  );
};

export default CommunityStatsTab;
